import {
  computed,
  inject,
  onScopeDispose,
  provide,
  ref,
  shallowRef,
  toValue,
  watch,
  type ComputedRef,
  type InjectionKey,
  type MaybeRefOrGetter,
  type ShallowRef,
} from 'vue';

/**
 * Centralized keyboard-shortcut manager.
 *
 * One capture-phase document keydown listener dispatches to registered
 * bindings. Overlays (modals, poppers, flyouts) push a layer while open;
 * the topmost layer's bindings are offered the event first, so "Escape
 * closes the topmost overlay" falls out of layer order instead of the
 * per-component DOM sniffing this module replaces.
 *
 * While focus is in an editable element, bindings are skipped by default
 * (`allowInInput: false`) — element-level handlers own the keyboard there.
 * An unconsumed Escape that bubbles back to the document blurs the editable
 * element, so a second Escape reaches the overlay.
 */

export const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

/** The one shared "is the user typing here?" check. */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target instanceof HTMLTextAreaElement) return true;
  return target instanceof HTMLInputElement && !['checkbox', 'radio', 'file', 'button'].includes(target.type);
}

/** Return false (synchronously) to decline the event — it falls through to the next binding/layer. */
export type ShortcutHandler = (event: KeyboardEvent) => void | boolean | Promise<unknown>;

export type Unregister = () => void;

export interface ShortcutBindingOptions {
  enabled?: MaybeRefOrGetter<boolean>;
  /** 'bubble': while typing, dispatch AFTER element handlers (they can shield via stop/preventDefault). */
  allowInInput?: boolean | 'bubble';
  preventDefault?: boolean;
  stopPropagation?: boolean;
  /** A function is re-evaluated on display, for labels that load async (e.g. brand-specific names). */
  description?: string | (() => string);
  /** Grouping label in the shortcut-help overlay. Defaults to the layer name, or 'This page'. */
  scope?: string;
  /** Footnote for the chord-hint popup, explaining a variant of the sequence it belongs to. */
  hint?: string;
}

export interface ParsedChord {
  key?: string;
  code?: string;
  ctrl: boolean;
  alt: boolean;
  shift: boolean;
  meta: boolean;
}

export interface PrimaryActionOptions {
  enabled?: MaybeRefOrGetter<boolean>;
  /** When set, the action is skipped while the element is unmounted or CSS-hidden. */
  element?: MaybeRefOrGetter<HTMLElement | null | undefined>;
  description?: string | (() => string);
  scope?: string;
}

export interface ShortcutLayerHandle {
  register(combo: string | string[], handler: ShortcutHandler, options?: ShortcutBindingOptions): Unregister;
  registerPrimaryAction(run: (event: KeyboardEvent) => unknown, options?: PrimaryActionOptions): Unregister;
  activate(): void;
  deactivate(): void;
  destroy(): void;
  /** True when no other layer sits above this one (nested popper, modal-from-popper, …). */
  isTopmost(): boolean;
}

interface Binding {
  chords: ParsedChord[];
  combo: string;
  handler: ShortcutHandler;
  enabled: MaybeRefOrGetter<boolean>;
  allowInInput: boolean | 'bubble';
  preventDefault: boolean;
  stopPropagation: boolean;
  description?: string | (() => string);
  scope?: string;
  hint?: string;
  /** Shared by the bindings of one registration, so the help overlay lists them together. */
  group?: symbol;
}

function describeBinding(binding: Binding): string | undefined {
  return typeof binding.description === 'function' ? binding.description() : binding.description;
}

interface PrimaryActionBinding {
  run: (event: KeyboardEvent) => unknown;
  enabled: MaybeRefOrGetter<boolean>;
  element?: MaybeRefOrGetter<HTMLElement | null | undefined>;
  description?: string | (() => string);
  scope?: string;
}

function describeAction(action: PrimaryActionBinding): string | undefined {
  return typeof action.description === 'function' ? action.description() : action.description;
}

interface Layer {
  name?: string;
  blockFallthrough: boolean;
  bindings: Binding[];
  primaryActions: PrimaryActionBinding[];
}

const MODIFIER_KEYS = new Set(['Shift', 'Control', 'Alt', 'Meta', 'AltGraph', 'CapsLock']);

export const SEQUENCE_TIMEOUT_MS = 3000;

// --- Combo parsing -----------------------------------------------------------

function parseChord(chord: string, mac: boolean): ParsedChord {
  const parsed: ParsedChord = { ctrl: false, alt: false, shift: false, meta: false };
  const tokens = chord.split('+');
  const keyToken = tokens.pop() ?? '';

  for (const token of tokens) {
    switch (token.toLowerCase()) {
      case 'ctrl':
        parsed.ctrl = true;
        break;
      case 'alt':
        parsed.alt = true;
        break;
      case 'shift':
        parsed.shift = true;
        break;
      case 'meta':
        parsed.meta = true;
        break;
      case 'mod':
        if (mac) parsed.meta = true;
        else parsed.ctrl = true;
        break;
      default:
        throw new Error(`Unknown modifier "${token}" in shortcut chord "${chord}"`);
    }
  }

  if (keyToken.startsWith('code:')) {
    parsed.code = keyToken.slice('code:'.length);
  } else if (parsed.alt && /^[a-z0-9]$/i.test(keyToken)) {
    // The Mac Option layer rewrites event.key ('ç' for alt+c), so alt-chords
    // with a letter/digit match on the physical key instead.
    parsed.code = /^[0-9]$/.test(keyToken) ? `Digit${keyToken}` : `Key${keyToken.toUpperCase()}`;
  } else {
    parsed.key = keyToken.toLowerCase();
  }

  return parsed;
}

/** Chords are '+'-joined, sequence steps are space-separated: 'mod+k', 'g s'. */
export function parseCombo(combo: string, opts?: { isMac?: boolean }): ParsedChord[] {
  const mac = opts?.isMac ?? isMac;
  const chords = combo.trim().split(/\s+/);
  if (!chords.length || chords.some((c) => !c)) throw new Error(`Empty shortcut combo "${combo}"`);
  return chords.map((chord) => parseChord(chord, mac));
}

function chordMatches(chord: ParsedChord, event: KeyboardEvent): boolean {
  if (chord.ctrl !== event.ctrlKey) return false;
  if (chord.alt !== event.altKey) return false;
  if (chord.meta !== event.metaKey) return false;

  // For printable non-alphanumeric keys ('?', '/') event.key already reflects
  // the shift state of the user's layout, so the shift bit is ignored.
  const shiftAgnostic = chord.key !== undefined && chord.key.length === 1 && !/[a-z0-9]/.test(chord.key);
  if (!shiftAgnostic && chord.shift !== event.shiftKey) return false;

  if (chord.code !== undefined) return event.code === chord.code;
  return event.key.toLowerCase() === chord.key;
}

// --- Registry ----------------------------------------------------------------

const baseLayer: Layer = { name: undefined, blockFallthrough: false, bindings: [], primaryActions: [] };
const layerStack = shallowRef<Layer[]>([]);
const registryVersion = ref(0);

let bindingCount = 0;
let listenersBound = false;

function bumpRegistry() {
  registryVersion.value++;
}

function ensureListeners() {
  if (listenersBound || typeof document === 'undefined') return;
  document.addEventListener('keydown', onKeydownCapture, { capture: true });
  document.addEventListener('keydown', onKeydownBubble);
  listenersBound = true;
}

function releaseListeners() {
  if (!listenersBound || bindingCount > 0) return;
  document.removeEventListener('keydown', onKeydownCapture, { capture: true });
  document.removeEventListener('keydown', onKeydownBubble);
  listenersBound = false;
}

function addBinding(layer: Layer, binding: Binding): Unregister {
  layer.bindings.push(binding);
  bindingCount++;
  ensureListeners();
  bumpRegistry();

  return () => {
    const index = layer.bindings.indexOf(binding);
    // Already gone: unregistered before, or the layer was destroyed first
    // (Vue stops a parent's scope before unmounting its children, so a
    // layer's destroy() can precede its descendants' unregister calls).
    if (index === -1) return;
    layer.bindings.splice(index, 1);
    bindingCount--;
    releaseListeners();
    bumpRegistry();
  };
}

function createBindings(
  layer: Layer,
  combo: string | string[],
  handler: ShortcutHandler,
  options?: ShortcutBindingOptions
): Unregister {
  const combos = Array.isArray(combo) ? combo : [combo];
  // One registration, one row in the help overlay, however many keys reach it.
  const group = combos.length > 1 ? Symbol('shortcut-group') : undefined;
  const unregisters = combos.map((c) =>
    addBinding(layer, {
      chords: parseCombo(c),
      combo: c,
      group,
      handler,
      enabled: options?.enabled ?? true,
      allowInInput: options?.allowInInput ?? false,
      preventDefault: options?.preventDefault ?? true,
      stopPropagation: options?.stopPropagation ?? true,
      description: options?.description,
      scope: options?.scope,
      hint: options?.hint,
    })
  );
  return () => unregisters.forEach((unregister) => unregister());
}

function addPrimaryAction(
  layer: Layer,
  run: (event: KeyboardEvent) => unknown,
  options?: PrimaryActionOptions
): Unregister {
  const action: PrimaryActionBinding = {
    run,
    enabled: options?.enabled ?? true,
    element: options?.element,
    description: options?.description,
    scope: options?.scope,
  };
  layer.primaryActions.push(action);
  bindingCount++;
  ensureListeners();
  bumpRegistry();

  return () => {
    const index = layer.primaryActions.indexOf(action);
    if (index === -1) return; // already unregistered, or the layer was destroyed first
    layer.primaryActions.splice(index, 1);
    bindingCount--;
    releaseListeners();
    bumpRegistry();
  };
}

// --- Layers ------------------------------------------------------------------

export function createShortcutLayer(name?: string, opts?: { blockFallthrough?: boolean }): ShortcutLayerHandle {
  const layer: Layer = { name, blockFallthrough: opts?.blockFallthrough ?? false, bindings: [], primaryActions: [] };
  let destroyed = false;

  const deactivate = () => {
    if (!layerStack.value.includes(layer)) return;
    layerStack.value = layerStack.value.filter((l) => l !== layer);
    bumpRegistry();
  };

  return {
    register(combo, handler, options) {
      if (destroyed) return () => {};
      return createBindings(layer, combo, handler, options);
    },
    registerPrimaryAction(run, options) {
      if (destroyed) return () => {};
      return addPrimaryAction(layer, run, options);
    },
    activate() {
      if (destroyed) return;
      // Re-activating moves the layer to the top of the stack.
      layerStack.value = [...layerStack.value.filter((l) => l !== layer), layer];
      bumpRegistry();
    },
    deactivate,
    destroy() {
      if (destroyed) return;
      destroyed = true;
      deactivate();
      bindingCount -= layer.bindings.length + layer.primaryActions.length;
      layer.bindings = [];
      layer.primaryActions = [];
      releaseListeners();
      bumpRegistry();
    },
    isTopmost() {
      return layerStack.value[layerStack.value.length - 1] === layer;
    },
  };
}

const shortcutLayerKey: InjectionKey<ShortcutLayerHandle> = Symbol('shortcut-layer');

/**
 * Creates a layer that follows `isActive` and is cleaned up with the component.
 * Descendant `useShortcut()` calls (teleported content included) attach to it.
 */
export function useShortcutLayer(
  isActive: MaybeRefOrGetter<boolean>,
  name?: string,
  opts?: { blockFallthrough?: boolean }
): Pick<ShortcutLayerHandle, 'register'> {
  const handle = createShortcutLayer(name, opts);

  watch(
    () => toValue(isActive),
    (active) => (active ? handle.activate() : handle.deactivate()),
    { immediate: true }
  );
  onScopeDispose(() => handle.destroy());
  provide(shortcutLayerKey, handle);

  return { register: handle.register };
}

/** Registers on the nearest ancestor layer, or the base (page) layer. */
export function useShortcut(
  combo: string | string[],
  handler: ShortcutHandler,
  options?: ShortcutBindingOptions
): void {
  const layer = inject(shortcutLayerKey, null);
  const unregister = layer
    ? layer.register(combo, handler, options)
    : createBindings(baseLayer, combo, handler, options);
  onScopeDispose(unregister);
}

/**
 * Registers the context's primary action — what (Ctrl+)Enter runs. Attaches to
 * the nearest ancestor layer (modal/popper/flyout), or the base (page) layer.
 * The last registered visible action within a layer wins.
 */
export function usePrimaryAction(run: (event: KeyboardEvent) => unknown, options?: PrimaryActionOptions): void {
  const layer = inject(shortcutLayerKey, null);
  const unregister = layer ? layer.registerPrimaryAction(run, options) : addPrimaryAction(baseLayer, run, options);
  onScopeDispose(unregister);
}

/**
 * Options-API bridge: provides an existing layer handle to descendants
 * (slot and teleported content included). Must be called during setup().
 */
export function provideShortcutLayer(handle: ShortcutLayerHandle): void {
  provide(shortcutLayerKey, handle);
}

// --- Sequences ---------------------------------------------------------------

interface PendingCandidate {
  binding: Binding;
  layer: Layer;
}

export interface PendingSequence {
  prefix: string;
  candidates: { key: string; combo: string; description?: string }[];
  /** Footnote from any candidate that carries one, e.g. how to open in a new tab. */
  hint?: string;
}

export const pendingSequence: ShallowRef<PendingSequence | null> = shallowRef(null);

let pendingCandidates: PendingCandidate[] = [];
let pendingChordIndex = 0;
let pendingTimer: ReturnType<typeof setTimeout> | undefined;

export function cancelPendingSequence(): void {
  if (!pendingSequence.value) return;
  clearTimeout(pendingTimer);
  pendingCandidates = [];
  pendingChordIndex = 0;
  pendingSequence.value = null;
  if (typeof document !== 'undefined') document.removeEventListener('mousedown', cancelPendingSequence);
}

function chordLabel(chord: ParsedChord): string {
  if (chord.key !== undefined) return chord.key;
  if (chord.code !== undefined) return chord.code.replace(/^(Key|Digit)/, '').toLowerCase();
  return '';
}

function setPendingSequence(prefix: string, candidates: PendingCandidate[], chordIndex: number) {
  clearTimeout(pendingTimer);
  pendingCandidates = candidates;
  pendingChordIndex = chordIndex;
  pendingSequence.value = {
    prefix,
    candidates: candidates.map(({ binding }) => ({
      key: chordLabel(binding.chords[chordIndex]),
      combo: binding.combo,
      description: describeBinding(binding),
    })),
    hint: candidates.find(({ binding }) => binding.hint)?.binding.hint,
  };
  pendingTimer = setTimeout(cancelPendingSequence, SEQUENCE_TIMEOUT_MS);
  document.addEventListener('mousedown', cancelPendingSequence);
}

function bindingIsAvailable(binding: Binding, event: KeyboardEvent): boolean {
  if (!toValue(binding.enabled)) return false;
  if (binding.allowInInput !== true && isEditableTarget(event.target)) return false;
  return true;
}

/** Returns true when the event was consumed by the pending sequence. */
function handlePendingSequence(event: KeyboardEvent): boolean {
  if (event.key === 'Escape') {
    cancelPendingSequence();
    event.preventDefault();
    event.stopPropagation();
    return true;
  }

  const chordIndex = pendingChordIndex;
  const advancing = pendingCandidates.filter(
    ({ binding }) => chordMatches(binding.chords[chordIndex], event) && bindingIsAvailable(binding, event)
  );

  if (!advancing.length) {
    // Not part of the sequence — dismiss and let the key match fresh below.
    cancelPendingSequence();
    return false;
  }

  const complete = advancing.find(({ binding }) => binding.chords.length === chordIndex + 1);
  if (complete) {
    cancelPendingSequence();
    const result = complete.binding.handler(event);
    if (result !== false) {
      if (complete.binding.preventDefault) event.preventDefault();
      if (complete.binding.stopPropagation) event.stopPropagation();
      return true;
    }
    return false;
  }

  const prefix = `${pendingSequence.value?.prefix} ${event.key.toLowerCase()}`;
  setPendingSequence(
    prefix,
    advancing.filter(({ binding }) => binding.chords.length > chordIndex + 1),
    chordIndex + 1
  );
  event.preventDefault();
  event.stopPropagation();
  return true;
}

// --- Dispatch ----------------------------------------------------------------

function onKeydownCapture(event: KeyboardEvent) {
  if (MODIFIER_KEYS.has(event.key)) return;

  if (pendingSequence.value && handlePendingSequence(event)) return;

  const sequenceCandidates: PendingCandidate[] = [];
  const layers = [...layerStack.value].reverse();

  for (const layer of [...layers, baseLayer]) {
    // Most recently registered first, matching paste-upload's last-wins rule.
    for (let i = layer.bindings.length - 1; i >= 0; i--) {
      const binding = layer.bindings[i];
      if (!chordMatches(binding.chords[0], event)) continue;
      if (!bindingIsAvailable(binding, event)) continue;

      if (binding.chords.length > 1) {
        sequenceCandidates.push({ binding, layer });
        continue;
      }

      const result = binding.handler(event);
      if (result === false) continue;
      if (binding.preventDefault) event.preventDefault();
      if (binding.stopPropagation) event.stopPropagation();
      return;
    }
    if (layer.blockFallthrough) break;
  }

  if (sequenceCandidates.length) {
    setPendingSequence(event.key.toLowerCase(), sequenceCandidates, 1);
    event.preventDefault();
    event.stopPropagation();
  }
}

/**
 * Bubble-phase handling — deliberately AFTER element handlers, so components
 * that stopPropagation or preventDefault shield it without any coordination:
 *
 * - Escape that reached the document unconsumed while focus is in an editable
 *   element blurs it, so the next Escape goes to the overlay. (Blurring in
 *   capture would fire save-on-blur before e.g. fs-input-text could revert.)
 * - Enter dispatches the topmost layer's primary action (see dispatchPrimaryAction).
 */
function onKeydownBubble(event: KeyboardEvent) {
  if (event.key === 'Enter') {
    dispatchPrimaryAction(event);
    return;
  }

  if (event.key !== 'Escape') return;
  if (!isEditableTarget(event.target)) return;
  if (event.defaultPrevented) return;

  // Bindings opted into `allowInInput: 'bubble'` (e.g. a table cell's discard)
  // run here, after element handlers had their chance to shield them.
  if (dispatchBubbleEscape(event)) return;

  const active = document.activeElement;
  if (active instanceof HTMLElement && isEditableTarget(active)) active.blur();
}

function dispatchBubbleEscape(event: KeyboardEvent): boolean {
  const layers = [...layerStack.value].reverse();
  for (const layer of [...layers, baseLayer]) {
    for (let i = layer.bindings.length - 1; i >= 0; i--) {
      const binding = layer.bindings[i];
      if (binding.chords.length > 1 || !chordMatches(binding.chords[0], event)) continue;
      if (!toValue(binding.enabled)) continue;

      // A capture-owning Escape binding above (e.g. a modal) keeps its
      // two-step blur-then-close semantics — layers below can't steal it.
      if (binding.allowInInput !== 'bubble') return false;

      const result = binding.handler(event);
      if (result === false) continue;
      if (binding.preventDefault) event.preventDefault();
      if (binding.stopPropagation) event.stopPropagation();
      return true;
    }
    if (layer.blockFallthrough) break;
  }
  return false;
}

function isMultilineTarget(target: EventTarget | null): boolean {
  if (target instanceof HTMLTextAreaElement) return true;
  return target instanceof HTMLElement && target.isContentEditable;
}

function actionIsVisible(action: PrimaryActionBinding): boolean {
  if (action.element === undefined) return true;
  const el = toValue(action.element);
  if (!el || !el.isConnected) return false;
  // slides-container keeps inactive frames mounted with visibility: hidden —
  // a hidden button-pair must not win over a visible one. visibilityProperty
  // must be requested explicitly (older engines use checkVisibilityCSS).
  if (typeof el.checkVisibility === 'function') {
    return el.checkVisibility({ visibilityProperty: true, checkVisibilityCSS: true } as CheckVisibilityOptions);
  }
  const style = getComputedStyle(el);
  return style.display !== 'none' && style.visibility !== 'hidden';
}

function dispatchPrimaryAction(event: KeyboardEvent) {
  if (event.repeat || event.defaultPrevented) return;
  if (event.altKey || event.shiftKey) return; // Shift+Enter stays "newline"

  const mod = event.ctrlKey || event.metaKey;
  if (!mod) {
    // Plain Enter fires only from single-line editables or with nothing
    // focused. On buttons/links/toggles native activation must win, and in
    // multiline fields Enter means "newline" — mod+Enter commits from there.
    if (isMultilineTarget(event.target)) return;
    const background = event.target === document.body || event.target === document.documentElement;
    if (!background && !isEditableTarget(event.target)) return;
  }

  const layer = layerStack.value[layerStack.value.length - 1] ?? baseLayer;
  // Last registered visible action wins; a disabled one blocks rather than
  // falling through, so Enter never surprises with a lower/hidden action.
  for (let i = layer.primaryActions.length - 1; i >= 0; i--) {
    const action = layer.primaryActions[i];
    if (!actionIsVisible(action)) continue;
    if (!toValue(action.enabled)) return;
    event.preventDefault();
    event.stopPropagation();
    action.run(event);
    return;
  }
}

// --- Introspection (help overlay) --------------------------------------------

export interface ActiveBinding {
  /**
   * Every key that runs this action. A registration made under several keys — Delete and
   * Backspace for the same clear — is one row in the overlay rather than one per key.
   */
  combos: string[];
  description?: string;
  scope: string;
}

/**
 * Currently registered, enabled bindings that carry a description — what the
 * help overlay renders. Deduplicated per combo the way dispatch works: the
 * topmost layer's binding wins and shadowed ones are omitted. Undescribed
 * bindings (overlay chrome like the help dialog's own Escape) never claim a
 * combo, so they stay invisible to this listing.
 */
export const activeBindings: ComputedRef<ActiveBinding[]> = computed(() => {
  void registryVersion.value;
  const result: ActiveBinding[] = [];
  const seen = new Set<string>();
  const grouped = new Map<symbol, ActiveBinding>();

  const topLayer = layerStack.value[layerStack.value.length - 1] ?? baseLayer;

  const collect = (layer: Layer) => {
    // Newest registrations win within a layer (matching dispatch order), but
    // rows are emitted in registration order so the listing reads naturally.
    const winners = new Set<Binding | PrimaryActionBinding>();
    for (let i = layer.bindings.length - 1; i >= 0; i--) {
      const binding = layer.bindings[i];
      if (!binding.description || !toValue(binding.enabled)) continue;
      const key = binding.combo.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      winners.add(binding);
    }
    // Unlike key bindings, the Enter action never falls through: dispatch
    // consults the topmost layer only.
    if (layer === topLayer) {
      for (let i = layer.primaryActions.length - 1; i >= 0; i--) {
        const action = layer.primaryActions[i];
        if (!action.description || !toValue(action.enabled) || !actionIsVisible(action)) continue;
        if (seen.has('enter')) continue;
        seen.add('enter');
        winners.add(action);
      }
    }

    for (const binding of layer.bindings) {
      if (!winners.has(binding)) continue;

      const sibling = binding.group ? grouped.get(binding.group) : undefined;
      if (sibling) {
        sibling.combos.push(binding.combo);
        continue;
      }

      const entry: ActiveBinding = {
        combos: [binding.combo],
        description: describeBinding(binding),
        scope: binding.scope ?? layer.name ?? 'This page',
      };
      if (binding.group) grouped.set(binding.group, entry);
      result.push(entry);
    }
    for (const action of layer.primaryActions) {
      if (!winners.has(action)) continue;
      result.push({
        combos: ['enter'],
        description: describeAction(action),
        scope: action.scope ?? layer.name ?? 'This page',
      });
    }
  };

  for (let i = layerStack.value.length - 1; i >= 0; i--) collect(layerStack.value[i]);
  collect(baseLayer);
  return result;
});

const KEY_LABELS: Record<string, string> = {
  escape: 'Esc',
  arrowleft: '←',
  arrowright: '→',
  arrowup: '↑',
  arrowdown: '↓',
  enter: 'Enter',
  ' ': 'Space',
};

/** Display chips for a combo: ['⌘', 'K'], ['Ctrl', 'K'], ['g', 'then', 'p']. */
export function formatCombo(combo: string): string[] {
  const chips: string[] = [];
  parseCombo(combo).forEach((chord, index) => {
    if (index > 0) chips.push('then');
    if (chord.ctrl) chips.push(isMac ? '⌃' : 'Ctrl');
    if (chord.alt) chips.push(isMac ? '⌥' : 'Alt');
    if (chord.shift) chips.push(isMac ? '⇧' : 'Shift');
    if (chord.meta) chips.push(isMac ? '⌘' : 'Win');
    const label = chordLabel(chord);
    chips.push(KEY_LABELS[label] ?? (label.length === 1 ? label.toUpperCase() : label));
  });
  return chips;
}
